// Firebase provider — presence channel data access.
//
// Relocated (S2) from src/features/chat/presence.js, verbatim except: every
// Firestore call now uses the lazy `getDb()` instead of a module-level `db`,
// and `reportFirestoreError` is imported from ../../firebase-health.js (the
// original module referenced it without importing it — a latent bug that only
// fires inside the best-effort catch blocks; fixed here so the contract's
// "report write failures to the health bus" guarantee actually holds).
//
// Schema (Firestore):
//   presence/{partyCode}/users/{userId} = {
//     handle, avatarSeed, lastSeen: serverTimestamp()
//   }
//
// Presence is best-effort: a missed heartbeat just ages the entry out of the
// 3-minute window. Writes never surface errors to the user.
import {
  collection,
  doc,
  onSnapshot,
} from '@firebase/firestore';
import { setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { getDb } from './client.js';
import { reportFirestoreError } from '../../firebase-health.js';

// Activity windows.
export const PRESENCE_ACTIVE_MS = 180_000; // 3 min — past this we drop the row
export const PRESENCE_HEARTBEAT_MS = 25_000; // 25 s — refresh cadence

// Permitted optional fields on the presence doc. Beam (F10.1/F10.2) layers
// in `publicIp` (string|null IPv4) and `deviceType` ('phone'|'monitor').
// Future additive extensions get added here; unknown keys are dropped so the
// schema can't drift through typos.
const ALLOWED_EXTRAS = new Set(['publicIp', 'deviceType']);

function sanitizeExtras(extras) {
  if (!extras || typeof extras !== 'object') return null;
  const out = {};
  let hasAny = false;
  for (const [key, value] of Object.entries(extras)) {
    if (!ALLOWED_EXTRAS.has(key)) continue;
    if (value === undefined) continue;
    out[key] = value;
    hasAny = true;
  }
  return hasAny ? out : null;
}

/**
 * Join `code` as `userId` with display metadata. Idempotent: the same call
 * either creates or refreshes the doc, so it doubles as a heartbeat. We
 * always set { merge: false } so a stale `expiresAt`-like field can never
 * linger across schema changes.
 *
 * Optional `extras` lets Beam piggyback `publicIp` + `deviceType` onto the
 * existing presence write. Unknown keys are dropped — see ALLOWED_EXTRAS.
 */
export async function joinPresence(code, userId, handle, avatarSeed, extras) {
  if (!code || !userId) return;
  const extra = sanitizeExtras(extras);
  try {
    const payload = {
      handle: handle || '',
      avatarSeed: avatarSeed || userId,
      lastSeen: serverTimestamp(),
    };
    if (extra) Object.assign(payload, extra);
    await setDoc(doc(getDb(), 'presence', code, 'users', userId), payload);
  } catch (err) {
    // Best-effort — but surface quota / permission errors via the health
    // bus so the user sees why their presence stopped propagating.
    reportFirestoreError(err);
  }
}

/**
 * Heartbeat — refresh `lastSeen`. We re-use joinPresence so the handle /
 * avatarSeed stay current if the user changed their handle mid-session.
 */
export async function heartbeat(code, userId, handle, avatarSeed, extras) {
  return joinPresence(code, userId, handle, avatarSeed, extras);
}

/**
 * Leave: best-effort delete. If the network is gone, the entry will age out
 * of the 3-minute window on the watching clients' side.
 */
export async function leavePresence(code, userId) {
  if (!code || !userId) return;
  try {
    await deleteDoc(doc(getDb(), 'presence', code, 'users', userId));
  } catch (err) {
    reportFirestoreError(err);
  }
}

// Firestore Timestamp → epoch ms. Tolerates plain numbers and null.
function tsToMs(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value.toMillis === 'function') {
    try {
      return value.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof value.seconds === 'number') {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
  }
  return 0;
}

/**
 * Subscribe to presence for `code`. Yields entries whose `lastSeen` is within
 * the active window (3 min). Returns the unsubscribe function.
 *
 * Each entry: { id, handle, avatarSeed, lastSeen (ms) }.
 */
export function subscribePresence(code, cb) {
  if (!code) return () => {};
  return onSnapshot(
    collection(getDb(), 'presence', code, 'users'),
    (snapshot) => {
      const now = Date.now();
      const rows = [];
      snapshot.forEach((d) => {
        const data = d.data() || {};
        const lastSeenMs = tsToMs(data.lastSeen);
        // Allow lastSeenMs === 0 — a freshly-written row's serverTimestamp may
        // not have resolved yet. Treat as "just now" so we don't flash-hide
        // the user's own avatar between write and snapshot.
        if (lastSeenMs !== 0 && now - lastSeenMs > PRESENCE_ACTIVE_MS) return;
        // publicIp + deviceType are additive — older docs without them read as
        // null / 'monitor'. Treat 'monitor' as the safe default for the UA
        // heuristic so peers that never set it don't all look like phones.
        rows.push({
          id: d.id,
          handle: data.handle || '',
          avatarSeed: data.avatarSeed || d.id,
          lastSeen: lastSeenMs || now,
          publicIp: typeof data.publicIp === 'string' ? data.publicIp : null,
          deviceType: data.deviceType === 'phone' ? 'phone' : 'monitor',
        });
      });
      cb(rows);
    },
    // onError: presence subscription itself failed (e.g. quota exceeded, or
    // permissions changed under us). Surface so the user knows real-time
    // sync is degraded — otherwise the radar just looks suspiciously empty.
    (err) => reportFirestoreError(err),
  );
}

export const __test__ = { tsToMs };
